/**
 * Página "ProPoints" para jugadores
 * 
 * Muestra:
 * - Sistema de puntuación ProPoints
 * - Ranking y comparativas
 * - Historial detallado
 * - Objetivos y logros
 */

import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { formatFechaPartido } from '../../utils/fechaUtils';

const PlayerProPoints = () => {
  const { playerData } = useOutletContext();
  const [historialProPoints, setHistorialProPoints] = useState(null);
  const [loadingHistorial, setLoadingHistorial] = useState(true);
  const [errorHistorial, setErrorHistorial] = useState(null);
  const [logros, setLogros] = useState(null);
  const [loadingLogros, setLoadingLogros] = useState(true);
  const [nivelData, setNivelData] = useState(null);
  const [loadingNivel, setLoadingNivel] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Estado para selector de temporada/torneo
  const [temporadas, setTemporadas] = useState([]);
  const [selectedTemporadaId, setSelectedTemporadaId] = useState(null);
  const [selectedTorneoId, setSelectedTorneoId] = useState(null);
  const [loadingTorneos, setLoadingTorneos] = useState(true);

  // Función para normalizar nombre de logro
  const normalizeLogroNombre = (logroNombre) => {
    return logroNombre
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, '_');
  };

  // Función para obtener imagen según logro y género
  const getLogroImage = (nombreLogro, genero) => {
    const esFemenino = genero === 'F';
    const normalizedNombre = normalizeLogroNombre(nombreLogro);

    const imageMapMasculino = {
      'participacion': '/images/jugadorcampo-1x.jpeg',
      'victoria': '/images/jugadorcampo-1x.jpeg',
      'destacado': '/images/jugadorcampo-1x.jpeg',
      'el_jugon': '/images/eljugon-1x.jpeg',
      'el_cerrojo': '/images/cerrojo-1x.jpeg',
      'el_desequilibrante': '/images/desequilibrante-1x.jpeg',
      'el_lider': '/images/lider-1x.jpeg',
      'el_mejor_companero': '/images/mejorcompanero-1x.jpeg',
      'mayor_progresion': '/images/progresion-1x.jpeg',
      'constancia': '/images/constante-1x.jpeg',
      'ganador_incansable': '/images/incansable-1x.jpeg',
      'portero': '/images/portero-1x.jpeg'
    };

    const imageMapFemenino = {
      'participacion': '/images/la_jugadorcampo.png',
      'victoria': '/images/la_jugadorcampo.png',
      'destacado': '/images/la_jugadorcampo.png',
      'el_jugon': '/images/la_eljugon.png',
      'el_cerrojo': '/images/la_cerrojo.png',
      'el_desequilibrante': '/images/la_desequilibrante.png',
      'el_lider': '/images/la_lider.png',
      'el_mejor_companero': '/images/la_mejorcompanero.png',
      'mayor_progresion': '/images/la_progresion.png',
      'constancia': '/images/la_constante.png',
      'ganador_incansable': '/images/la_incansable.png',
      'portero': '/images/la_portero.png'
    };

    const imageMap = esFemenino ? imageMapFemenino : imageMapMasculino;
    const defaultImage = esFemenino ? '/images/la_jugadorcampo.png' : '/images/jugadorcampo-1x.jpeg';

    return imageMap[normalizedNombre] || defaultImage;
  };

  // Función para renderizar logro con imagen o texto
  const renderLogro = (logro, isMobile) => {
    const genero = playerData?.gender === 'female' ? 'F' : 'M';
    const imageSrc = getLogroImage(logro.logro, genero);

    // Siempre mostrar como carta (tanto en móvil como desktop)
    return (
        <div key={logro.id} style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          position: 'relative',
          margin: '8px'
        }}>
          <div style={{
            position: 'relative',
            width: isMobile ? '80px' : '90px',
            height: isMobile ? '80px' : '90px',
            borderRadius: '15px',
            background: (logro.cantidad === 0 || logro.cantidad === "0" || !logro.cantidad)
              ? 'linear-gradient(135deg, #2C2C2C 0%, #1A1A1A 50%, #0D0D0D 100%)'
              : 'linear-gradient(135deg, #E8F5E8 0%, #C8E6C9 50%, #A5D6A7 100%)',
            border: `3px solid ${logro.cantidad > 0 ? '#4CAF50' : '#444444'}`,
            boxShadow: logro.cantidad > 0
              ? '0 8px 16px rgba(76, 175, 80, 0.4), inset 0 1px 0 rgba(255,255,255,0.3)'
              : '0 8px 20px rgba(0,0,0,0.6), inset 0 2px 4px rgba(0,0,0,0.8), inset 0 -2px 4px rgba(255,255,255,0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            transition: 'all 0.3s ease',
            cursor: 'pointer'
          }}>
            {(logro.cantidad === 0 || logro.cantidad === "0" || !logro.cantidad) ? (
              // Carta con interrogante si no tiene el logro
              <div style={{
                fontSize: isMobile ? '3rem' : '3.5rem',
                color: '#CCCCCC',
                fontWeight: 'bold',
                textShadow: '0 0 10px rgba(255,255,255,0.3), 2px 2px 4px rgba(0,0,0,0.8)',
                opacity: '0.9',
                filter: 'drop-shadow(0 0 8px rgba(255,255,255,0.2))'
              }}>
                ❓
              </div>
            ) : (
              // Imagen del logro si lo tiene
              <img
                src={imageSrc}
                alt={logro.logro}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
                onError={(e) => {
                  // Si la imagen no carga, mostrar icono alternativo
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            )}
            {/* Fallback si la imagen no carga */}
            <div style={{
              display: 'none',
              fontSize: '2rem',
              color: '#4CAF50',
              fontWeight: 'bold'
            }}>
              🏆
            </div>

          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: '8px',
            position: 'relative',
            paddingRight: '12px'
          }}>
            <span style={{
              fontSize: '0.7rem',
              color: logro.cantidad > 0 ? '#2E7D32' : '#CCCCCC',
              textAlign: 'center',
              maxWidth: '90px',
              lineHeight: '1.2',
              fontWeight: logro.cantidad > 0 ? '600' : '500',
              textShadow: (logro.cantidad === 0 || logro.cantidad === "0" || !logro.cantidad) ? '1px 1px 2px rgba(0,0,0,0.8)' : 'none'
            }}>
              {logro.logro}
            </span>
            {logro.cantidad > 0 && (
              <div style={{
                position: 'absolute',
                top: '-8px',
                right: '-20px',
                backgroundColor: '#FF4444',
                color: 'white',
                borderRadius: '50%',
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.8rem',
                fontWeight: 'bold',
                border: '2px solid white',
                boxShadow: '0 3px 6px rgba(0,0,0,0.4)',
                zIndex: 10
              }}>
                {logro.cantidad}
              </div>
            )}
          </div>
        </div>
      );
  };

  // Función para renderizar logro especial con imagen o texto
  const renderLogroEspecial = (logro, isMobile) => {
    const genero = playerData?.gender === 'female' ? 'F' : 'M';
    const imageSrc = getLogroImage(logro.logro, genero);

    // Siempre mostrar como carta (tanto en móvil como desktop)
    return (
        <div key={logro.id} style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          position: 'relative',
          margin: isMobile ? '4px' : '6px'
        }}>
          <div style={{
            position: 'relative',
            width: isMobile ? '72px' : '81px',
            height: isMobile ? '72px' : '81px',
            borderRadius: '15px',
            background: (logro.cantidad === 0 || logro.cantidad === "0" || !logro.cantidad)
              ? 'linear-gradient(135deg, #2C2C2C 0%, #1A1A1A 50%, #0D0D0D 100%)'
              : 'linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 50%, #FFCC02 100%)',
            border: `3px solid ${logro.cantidad > 0 ? '#FF9800' : '#444444'}`,
            boxShadow: logro.cantidad > 0
              ? '0 8px 16px rgba(255, 152, 0, 0.4), inset 0 1px 0 rgba(255,255,255,0.3)'
              : '0 8px 20px rgba(0,0,0,0.6), inset 0 2px 4px rgba(0,0,0,0.8), inset 0 -2px 4px rgba(255,255,255,0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            transition: 'all 0.3s ease',
            cursor: 'pointer'
          }}>
            {(logro.cantidad === 0 || logro.cantidad === "0" || !logro.cantidad) ? (
              // Carta con interrogante si no tiene el logro
              <div style={{
                fontSize: isMobile ? '3rem' : '3.5rem',
                color: '#CCCCCC',
                fontWeight: 'bold',
                textShadow: '0 0 10px rgba(255,255,255,0.3), 2px 2px 4px rgba(0,0,0,0.8)',
                opacity: '0.9',
                filter: 'drop-shadow(0 0 8px rgba(255,255,255,0.2))'
              }}>
                ❓
              </div>
            ) : (
              // Imagen del logro si lo tiene
              <img
                src={imageSrc}
                alt={logro.logro}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
                onError={(e) => {
                  // Si la imagen no carga, mostrar icono alternativo
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            )}
            {/* Fallback si la imagen no carga */}
            <div style={{
              display: 'none',
              fontSize: '2rem',
              color: '#FF9800',
              fontWeight: 'bold'
            }}>
              ⭐
            </div>

          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: '8px',
            position: 'relative',
            paddingRight: '12px'
          }}>
            <span style={{
              fontSize: '0.7rem',
              color: logro.cantidad > 0 ? '#E65100' : '#CCCCCC',
              textAlign: 'center',
              maxWidth: '90px',
              lineHeight: '1.2',
              fontWeight: logro.cantidad > 0 ? '600' : '500',
              textShadow: (logro.cantidad === 0 || logro.cantidad === "0" || !logro.cantidad) ? '1px 1px 2px rgba(0,0,0,0.8)' : 'none'
            }}>
              {logro.logro}
            </span>
            {logro.cantidad > 0 && (
              <div style={{
                position: 'absolute',
                top: '-8px',
                right: '-20px',
                backgroundColor: '#FF4444',
                color: 'white',
                borderRadius: '50%',
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.8rem',
                fontWeight: 'bold',
                border: '2px solid white',
                boxShadow: '0 3px 6px rgba(0,0,0,0.4)',
                zIndex: 10
              }}>
                {logro.cantidad}
              </div>
            )}
          </div>
        </div>
      );
  };

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  // Hook para manejar responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Cargar torneos del jugador (para selector)
  useEffect(() => {
    const fetchTorneos = async () => {
      try {
        setLoadingTorneos(true);
        const token = localStorage.getItem('prokids_token');
        const response = await fetch('/api/player/torneos', {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data.length > 0) {
            setTemporadas(result.data);
            const tempActiva = result.data.find(t => t.activa) || result.data[0];
            setSelectedTemporadaId(tempActiva.id);
            const torneoActivo = tempActiva.torneos.find(t => t.activo && t.inscripcion_activa) || tempActiva.torneos[0];
            if (torneoActivo) setSelectedTorneoId(torneoActivo.id);
          }
        }
      } catch (error) {
        console.error('Error al cargar torneos:', error);
      } finally {
        setLoadingTorneos(false);
      }
    };
    fetchTorneos();
  }, []);

  // Cargar historial detallado de ProPoints (depende del torneo seleccionado)
  useEffect(() => {
    const fetchHistorialProPoints = async () => {
      try {
        setLoadingHistorial(true);
        const token = localStorage.getItem('prokids_token');
        const url = selectedTorneoId
          ? `/api/player/propoints-history?torneoId=${selectedTorneoId}`
          : '/api/player/propoints-history';

        const response = await fetch(url, {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            setHistorialProPoints(result.data);
          } else {
            setErrorHistorial(result.message || 'Error al cargar historial');
          }
        } else {
          setErrorHistorial('Error al cargar historial de ProPoints');
        }
      } catch (error) {
        console.error('Error al cargar historial ProPoints:', error);
        setErrorHistorial('Error de conexión');
      } finally {
        setLoadingHistorial(false);
      }
    };

    if (selectedTorneoId !== null) fetchHistorialProPoints();
  }, [selectedTorneoId]);

  // Cargar logros del jugador (depende del torneo seleccionado)
  useEffect(() => {
    const fetchLogros = async () => {
      try {
        setLoadingLogros(true);
        const token = localStorage.getItem('prokids_token');
        const url = selectedTorneoId
          ? `/api/player/logros?torneoId=${selectedTorneoId}`
          : '/api/player/logros';

        const response = await fetch(url, {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            setLogros(result.data);
          } else {
            setLogros({ logros_individuales: [], logros_especiales: [] });
          }
        } else {
          setLogros({ logros_individuales: [], logros_especiales: [] });
        }
      } catch (error) {
        console.error('Error al cargar logros:', error);
        setLogros({ logros_individuales: [], logros_especiales: [] });
      } finally {
        setLoadingLogros(false);
      }
    };

    if (selectedTorneoId !== null) fetchLogros();
  }, [selectedTorneoId]);

  // Cargar datos del nivel del futbolista (depende del torneo seleccionado)
  useEffect(() => {
    const fetchNivelData = async () => {
      try {
        setLoadingNivel(true);
        const token = localStorage.getItem('prokids_token');
        const url = selectedTorneoId
          ? `/api/player/nivel?torneoId=${selectedTorneoId}`
          : '/api/player/nivel';

        const response = await fetch(url, {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success) setNivelData(result.data);
        }
      } catch (error) {
        console.error('Error al cargar datos del nivel:', error);
      } finally {
        setLoadingNivel(false);
      }
    };

    if (selectedTorneoId !== null) fetchNivelData();
  }, [selectedTorneoId]);

  const containerStyle = {
    maxWidth: '1000px',
    margin: '0 auto',
    padding: isMobile ? '0 15px' : '0 20px'
  };

  const cardStyle = {
    backgroundColor: 'white',
    borderRadius: '15px',
    padding: isMobile ? '20px' : '30px',
    marginBottom: '25px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    border: '1px solid #E8ECFF'
  };

  const titleStyle = {
    fontSize: isMobile ? '1.8rem' : '2.2rem',
    color: '#061156',
    fontWeight: '700',
    marginBottom: '30px',
    textAlign: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '15px'
  };

  const sectionTitleStyle = {
    fontSize: isMobile ? '1.4rem' : '1.6rem',
    color: '#061156',
    fontWeight: '700',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  };

  if (!playerData) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <p>Cargando ProPoints...</p>
      </div>
    );
  }

  const propoints = playerData.propoints || {};
  const partidosHistorial = historialProPoints?.partidos || [];

  return (
    <div style={containerStyle}>
      <h1 style={titleStyle}>
        ⭐ Sistema ProPoints™
      </h1>

      {/* Selector de Temporada y Torneo */}
      {temporadas.length > 0 && (
        <div style={{
          ...cardStyle,
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: '15px',
          padding: isMobile ? '15px' : '20px 25px',
          marginBottom: '20px',
          borderLeft: '4px solid #061156'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600', color: '#061156', whiteSpace: 'nowrap' }}>
            🏆 Torneo:
          </div>
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '10px', flex: 1 }}>
            <select
              value={selectedTemporadaId || ''}
              onChange={(e) => {
                const tempId = parseInt(e.target.value);
                setSelectedTemporadaId(tempId);
                const temp = temporadas.find(t => t.id === tempId);
                if (temp && temp.torneos.length > 0) {
                  const torneoActivo = temp.torneos.find(t => t.activo && t.inscripcion_activa) || temp.torneos[0];
                  setSelectedTorneoId(torneoActivo.id);
                }
              }}
              style={{
                padding: '10px 14px', borderRadius: '10px', border: '2px solid #E8ECFF',
                fontSize: '14px', fontWeight: '500', color: '#061156', backgroundColor: 'white',
                cursor: 'pointer', minWidth: isMobile ? '100%' : '200px'
              }}
            >
              {temporadas.map(temp => (
                <option key={temp.id} value={temp.id}>
                  📅 {temp.nombre} {temp.activa ? '(Actual)' : ''}
                </option>
              ))}
            </select>

            {(() => {
              const tempSeleccionada = temporadas.find(t => t.id === selectedTemporadaId);
              if (!tempSeleccionada || tempSeleccionada.torneos.length === 0) return null;
              return (
                <select
                  value={selectedTorneoId || ''}
                  onChange={(e) => setSelectedTorneoId(parseInt(e.target.value))}
                  style={{
                    padding: '10px 14px', borderRadius: '10px', border: '2px solid #E8ECFF',
                    fontSize: '14px', fontWeight: '500', color: '#061156', backgroundColor: 'white',
                    cursor: 'pointer', flex: 1, minWidth: isMobile ? '100%' : '250px'
                  }}
                >
                  {tempSeleccionada.torneos.map(torneo => (
                    <option key={torneo.id} value={torneo.id}>
                      {torneo.activo ? '🟢' : '🔴'} {torneo.nombre} {torneo.inscripcion_activa ? '(Inscrito)' : ''}
                    </option>
                  ))}
                </select>
              );
            })()}
          </div>
          {(() => {
            const tempSeleccionada = temporadas.find(t => t.id === selectedTemporadaId);
            const torneoSel = tempSeleccionada?.torneos.find(t => t.id === selectedTorneoId);
            if (!torneoSel || torneoSel.activo) return null;
            return (
              <div style={{
                backgroundColor: '#FFF3E0', color: '#E65100', padding: '8px 14px',
                borderRadius: '8px', fontSize: '13px', fontWeight: '500', whiteSpace: 'nowrap'
              }}>
                ⚠️ Torneo finalizado (histórico)
              </div>
            );
          })()}
        </div>
      )}

      {loadingTorneos && (
        <div style={{ ...cardStyle, textAlign: 'center', padding: '20px', marginBottom: '20px' }}>
          ⏳ Cargando torneos...
        </div>
      )}

      {/* Resumen de ProPoints */}
      <div style={cardStyle}>
        <h2 style={sectionTitleStyle}>
          📊 Tus ProPoints
        </h2>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
          gap: '18px',
          marginBottom: '20px'
        }}>
          <div style={{
            backgroundColor: '#4CAF50',
            color: 'white',
            padding: isMobile ? '20px 15px' : '25px',
            borderRadius: '15px',
            textAlign: 'center',
            boxShadow: '0 4px 15px rgba(76, 175, 80, 0.3)'
          }}>
            <div style={{
              fontSize: isMobile ? '1.8rem' : '2.2rem',
              fontWeight: '700',
              marginBottom: '5px'
            }}>
              {propoints.total || 0}
            </div>
            <div style={{
              fontSize: isMobile ? '0.85rem' : '0.95rem',
              opacity: 0.9,
              fontWeight: '500'
            }}>
              Total Acumulado
            </div>
          </div>
          
          <div style={{
            backgroundColor: '#2196F3',
            color: 'white',
            padding: isMobile ? '20px 15px' : '25px',
            borderRadius: '15px',
            textAlign: 'center',
            boxShadow: '0 4px 15px rgba(33, 150, 243, 0.3)'
          }}>
            <div style={{
              fontSize: isMobile ? '1.8rem' : '2.2rem',
              fontWeight: '700',
              marginBottom: '5px'
            }}>
              {propoints.temporada_actual || 0}
            </div>
            <div style={{
              fontSize: isMobile ? '0.85rem' : '0.95rem',
              opacity: 0.9,
              fontWeight: '500'
            }}>
              Esta Temporada
            </div>
          </div>
          
          <div style={{
            backgroundColor: '#FF9800',
            color: 'white',
            padding: isMobile ? '20px 15px' : '25px',
            borderRadius: '15px',
            textAlign: 'center',
            boxShadow: '0 4px 15px rgba(255, 152, 0, 0.3)'
          }}>
            <div style={{
              fontSize: isMobile ? '1.8rem' : '2.2rem',
              fontWeight: '700',
              marginBottom: '5px'
            }}>
              {propoints.ultima_jornada || 0}
            </div>
            <div style={{
              fontSize: isMobile ? '0.85rem' : '0.95rem',
              opacity: 0.9,
              fontWeight: '500'
            }}>
              Última Jornada
            </div>
          </div>
          
          <div style={{
            backgroundColor: nivelData?.nivel_actual?.color || '#CCCCCC',
            color: 'white',
            padding: isMobile ? '20px 15px' : '25px',
            borderRadius: '15px',
            textAlign: 'center',
            boxShadow: `0 4px 15px ${nivelData?.nivel_actual?.color ? nivelData.nivel_actual.color + '40' : 'rgba(204, 204, 204, 0.3)'}`
          }}>
            <div style={{
              fontSize: isMobile ? '1.8rem' : '2.2rem',
              fontWeight: '700',
              marginBottom: '5px'
            }}>
              {nivelData?.nivel_actual?.icono || '🎯'} {nivelData?.nivel_actual?.nivel || 'Sin nivel'}
            </div>
            <div style={{
              fontSize: isMobile ? '0.85rem' : '0.95rem',
              opacity: 0.9,
              fontWeight: '500'
            }}>
              {nivelData?.nivel_actual ? 'Nivel Actual' : 'Participa para obtener nivel'}
            </div>
          </div>
        </div>
        
        <div style={{
          backgroundColor: '#F8F9FF',
          padding: '15px',
          borderRadius: '10px',
          border: '2px solid #E8ECFF',
          textAlign: 'center'
        }}>
          <p style={{
            fontSize: '1rem',
            color: '#4352A9',
            margin: 0,
            fontWeight: '500'
          }}>
            💡 Los ProPoints™ se ganan por esfuerzo, rendimiento y valores deportivos en cada jornada
          </p>
        </div>
      </div>

      {/* Logros */}
      <div style={cardStyle}>
        <h2 style={sectionTitleStyle}>
          🏆 Logros
        </h2>

        {loadingLogros ? (
          <div style={{
            backgroundColor: '#F5F5F5',
            padding: '40px',
            borderRadius: '12px',
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '2rem',
              marginBottom: '10px'
            }}>⏳</div>
            <p style={{
              fontSize: '1.1rem',
              color: '#666',
              margin: 0,
              fontWeight: '500'
            }}>
              Cargando logros...
            </p>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: isMobile ? '15px' : '18px',
            marginBottom: '20px',
            padding: isMobile ? '0 5px' : '0 8px'
          }}>
            {/* Logros Individuales */}
            <div style={{
              backgroundColor: '#E8F5E8',
              padding: isMobile ? '18px 12px' : '18px',
              borderRadius: '12px',
              border: '2px solid #4CAF50'
            }}>
              <h3 style={{
                fontSize: '1.2rem',
                fontWeight: '700',
                color: '#2E7D32',
                margin: '0 0 15px 0',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                🎯 Logros Individuales
              </h3>

              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? 'repeat(3, 1fr)' : 'repeat(4, 1fr)',
                gap: isMobile ? '12px' : '14px',
                justifyItems: 'center',
                padding: isMobile ? '8px 0' : '10px 0',
                paddingLeft: isMobile ? '4px' : '6px'
              }}>
                {(logros?.logros_individuales && logros.logros_individuales.length > 0) ?
                  logros.logros_individuales.map((logro) => renderLogro(logro, isMobile)) :
                  // Mostrar cartas vacías si no hay logros
                  [
                    { id: 1, logro: 'Participación', cantidad: 0 },
                    { id: 2, logro: 'El Jugón', cantidad: 0 },
                    { id: 3, logro: 'El Cerrojo', cantidad: 0 },
                    { id: 4, logro: 'El Desequilibrante', cantidad: 0 },
                    { id: 5, logro: 'El Líder', cantidad: 0 },
                    { id: 6, logro: 'El Mejor Compañero', cantidad: 0 }
                  ].map((logro) => renderLogro(logro, isMobile))
                }
              </div>
            </div>

            {/* Logros Especiales */}
            <div style={{
              backgroundColor: '#FFF3E0',
              padding: isMobile ? '18px 12px' : '18px',
              borderRadius: '12px',
              border: '2px solid #FF9800'
            }}>
              <h3 style={{
                fontSize: '1.2rem',
                fontWeight: '700',
                color: '#E65100',
                margin: '0 0 15px 0',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                ✨ Logros Especiales
              </h3>

              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? 'repeat(3, 1fr)' : 'repeat(3, 1fr)',
                gap: isMobile ? '10px' : '12px',
                justifyItems: 'center',
                padding: isMobile ? '5px 0' : '8px 0',
                paddingLeft: isMobile ? '5px' : '8px'
              }}>
                {(logros?.logros_especiales && logros.logros_especiales.length > 0) ?
                  logros.logros_especiales.map((logro) => renderLogroEspecial(logro, isMobile)) :
                  // Mostrar cartas vacías si no hay logros especiales
                  [
                    { id: 1, logro: 'Mayor Progresión', cantidad: 0 },
                    { id: 2, logro: 'Constancia', cantidad: 0 },
                    { id: 3, logro: 'Ganador Incansable', cantidad: 0 }
                  ].map((logro) => renderLogroEspecial(logro, isMobile))
                }
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Progreso de Nivel */}
      <div style={cardStyle}>
        <h2 style={sectionTitleStyle}>
          🎯 Mi Nivel
        </h2>

        {nivelData ? (
          <div>
            {/* Nivel Actual */}
            <div style={{
              backgroundColor: '#F8F9FF',
              padding: isMobile ? '20px' : '25px',
              borderRadius: '15px',
              border: '2px solid #E8ECFF',
              marginBottom: '20px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '15px',
                flexWrap: 'wrap',
                gap: '10px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div style={{
                    fontSize: '2.5rem',
                    lineHeight: 1
                  }}>
                    {nivelData.nivel_actual?.icono || '🏆'}
                  </div>
                  <div>
                    <div style={{
                      fontSize: isMobile ? '1.3rem' : '1.5rem',
                      fontWeight: '700',
                      color: nivelData.nivel_actual?.color || '#061156',
                      marginBottom: '2px'
                    }}>
                      {nivelData.nivel_actual?.nivel || 'Inicial'}
                    </div>
                    <div style={{
                      fontSize: isMobile ? '0.9rem' : '1rem',
                      color: '#666',
                      fontWeight: '500'
                    }}>
                      {nivelData.nivel_actual?.descripcion || 'Nivel de inicio'}
                    </div>
                  </div>
                </div>
                <div style={{
                  textAlign: 'right'
                }}>
                  <div style={{
                    fontSize: isMobile ? '1.1rem' : '1.3rem',
                    fontWeight: '700',
                    color: '#4CAF50',
                    marginBottom: '2px'
                  }}>
                    {nivelData.propoints_acumulados || 0} PP
                  </div>
                  <div style={{
                    fontSize: isMobile ? '0.8rem' : '0.9rem',
                    color: '#666',
                    fontWeight: '500'
                  }}>
                    ProPoints Acumulados
                  </div>
                </div>
              </div>

              {/* Progreso hacia siguiente nivel */}
              {nivelData.siguiente_nivel ? (
                <div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '8px'
                  }}>
                    <span style={{
                      fontSize: isMobile ? '0.9rem' : '1rem',
                      color: '#666',
                      fontWeight: '500'
                    }}>
                      Progreso hacia {nivelData.siguiente_nivel.icono} {nivelData.siguiente_nivel.nivel}
                    </span>
                    <span style={{
                      fontSize: isMobile ? '0.8rem' : '0.9rem',
                      color: '#666',
                      fontWeight: '600'
                    }}>
                      {Math.round(nivelData.progreso)}%
                    </span>
                  </div>
                  <div style={{
                    width: '100%',
                    height: '12px',
                    backgroundColor: '#E0E0E0',
                    borderRadius: '6px',
                    overflow: 'hidden',
                    marginBottom: '8px'
                  }}>
                    <div style={{
                      width: `${nivelData.progreso}%`,
                      height: '100%',
                      background: `linear-gradient(90deg, ${nivelData.siguiente_nivel.color || '#4CAF50'}, ${nivelData.siguiente_nivel.color || '#4CAF50'}CC)`,
                      borderRadius: '6px',
                      transition: 'width 0.3s ease'
                    }}></div>
                  </div>
                  <div style={{
                    fontSize: isMobile ? '0.8rem' : '0.9rem',
                    color: '#666',
                    textAlign: 'center'
                  }}>
                    Necesitas <strong>{nivelData.puntos_para_siguiente}</strong> ProPoints más para alcanzar el siguiente nivel
                  </div>
                </div>
              ) : nivelData.nivel_actual ? (
                <div style={{
                  textAlign: 'center',
                  padding: '15px',
                  backgroundColor: '#FFF3E0',
                  borderRadius: '10px',
                  border: '2px solid #FFB74D'
                }}>
                  <div style={{
                    fontSize: isMobile ? '1.1rem' : '1.2rem',
                    fontWeight: '700',
                    color: '#E65100',
                    marginBottom: '5px'
                  }}>
                    🏆 ¡Nivel Máximo Alcanzado!
                  </div>
                  <div style={{
                    fontSize: isMobile ? '0.9rem' : '1rem',
                    color: '#E65100'
                  }}>
                    Has alcanzado el nivel más alto disponible
                  </div>
                </div>
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '15px',
                  backgroundColor: '#F5F5F5',
                  borderRadius: '10px',
                  border: '2px solid #CCCCCC'
                }}>
                  <div style={{
                    fontSize: isMobile ? '1.1rem' : '1.2rem',
                    fontWeight: '700',
                    color: '#666',
                    marginBottom: '5px'
                  }}>
                    🎯 ¡Comienza tu aventura!
                  </div>
                  <div style={{
                    fontSize: isMobile ? '0.9rem' : '1rem',
                    color: '#666'
                  }}>
                    Participa en jornadas para ganar ProPoints y subir de nivel
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div style={{
            textAlign: 'center',
            padding: '40px 20px',
            color: '#666'
          }}>
            {loadingNivel ? (
              <div>
                <div style={{ fontSize: '2rem', marginBottom: '10px' }}>⏳</div>
                <p>Cargando información de nivel...</p>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: '2rem', marginBottom: '10px' }}>❓</div>
                <p>No se pudo cargar la información del nivel</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Historial de ProPoints */}
      <div style={cardStyle}>
        <h2 style={sectionTitleStyle}>
          📈 Historial de ProPoints por Partido
        </h2>

        {loadingHistorial ? (
          <div style={{
            backgroundColor: '#F5F5F5',
            padding: '30px',
            borderRadius: '15px',
            textAlign: 'center'
          }}>
            <p style={{
              fontSize: '1.1rem',
              color: '#666',
              margin: 0,
              fontWeight: '500'
            }}>
              🔄 Cargando historial...
            </p>
          </div>
        ) : errorHistorial ? (
          <div style={{
            backgroundColor: '#FFE5E5',
            padding: '30px',
            borderRadius: '15px',
            textAlign: 'center',
            border: '2px solid #FF6B6B'
          }}>
            <p style={{
              fontSize: '1.1rem',
              color: '#D32F2F',
              margin: 0,
              fontWeight: '500'
            }}>
              ❌ {errorHistorial}
            </p>
          </div>
        ) : partidosHistorial.length > 0 ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '15px'
          }}>
            {partidosHistorial.map((partido) => (
              <div key={partido.partido_id} style={{
                backgroundColor: '#F8F9FF',
                padding: isMobile ? '15px' : '20px',
                borderRadius: '12px',
                border: '2px solid #E8ECFF'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  flexWrap: 'wrap',
                  gap: '15px',
                  marginBottom: '15px'
                }}>
                  <div>
                    <h4 style={{
                      fontSize: isMobile ? '1rem' : '1.1rem',
                      fontWeight: '700',
                      color: '#061156',
                      margin: '0 0 5px 0'
                    }}>
                      Jornada {partido.jornada_numero} - {partido.torneo_nombre}
                    </h4>
                    <p style={{
                      fontSize: isMobile ? '0.8rem' : '0.9rem',
                      color: '#666',
                      margin: '0 0 5px 0'
                    }}>
                      📅 {formatFechaPartido(partido.fecha_partido, partido.jornada_fecha, 'corto')} • 📍 {partido.sede_nombre}
                    </p>
                    <p style={{
                      fontSize: isMobile ? '0.75rem' : '0.85rem',
                      color: '#888',
                      margin: 0
                    }}>
                      🕐 {partido.hora} • 📊 Estado: {partido.estado}
                    </p>
                  </div>

                  <span style={{
                    backgroundColor: '#4CAF50',
                    color: 'white',
                    padding: isMobile ? '6px 12px' : '8px 16px',
                    borderRadius: '20px',
                    fontSize: isMobile ? '0.9rem' : '1rem',
                    fontWeight: '700'
                  }}>
                    +{partido.total_puntos} PP
                  </span>
                </div>

                {/* Desglose de logros */}
                <div style={{
                  backgroundColor: '#FFFFFF',
                  padding: isMobile ? '12px' : '15px',
                  borderRadius: '8px',
                  border: '1px solid #E0E0E0'
                }}>
                  <h5 style={{
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    color: '#061156',
                    margin: '0 0 10px 0'
                  }}>
                    🏆 Logros obtenidos:
                  </h5>
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px'
                  }}>
                    {partido.logros.map((logro, index) => (
                      <span key={index} style={{
                        backgroundColor: logro.puntos >= 2 ? '#FFD700' : '#E3F2FD',
                        color: logro.puntos >= 2 ? '#B8860B' : '#1976D2',
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '0.8rem',
                        fontWeight: '500'
                      }}>
                        {logro.nombre} (+{logro.puntos})
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{
            backgroundColor: '#F5F5F5',
            padding: '30px',
            borderRadius: '15px',
            textAlign: 'center',
            border: '2px dashed #CCCCCC'
          }}>
            <p style={{
              fontSize: '1.1rem',
              color: '#666',
              margin: 0,
              fontWeight: '500'
            }}>
              📋 Aún no tienes historial de ProPoints
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlayerProPoints;
